import { useEffect, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { AlertCircle, Save, Info } from "lucide-react";
import { ProductEntry } from "@/types/product/productStep.types";
import { useData } from "@/hooks/useData";
import { StoneType } from "@/types/stone-type/stone-type.types";
import { CommonFormFields } from "./shared/CommonFormFields";
import { useProductFormValidation } from "./shared/useProductFormValidation";
import {
  calculateExtractionProductFootprints,
  calculateProductRevenue,
  calculateRatioByQuantity,
  calculateRatioByRevenue,
} from "@/components/footprints/utils/calculations/extraction";
import { convertToTonnes } from "@/components/footprints/utils/calculations/unitConversions";
import { MaterialDocWithId } from "@/types/material";

interface ExtractionProductFormProps {
  formEntry: ProductEntry;
  onSave: (entry: ProductEntry) => void;
  onClose: () => void;
  editing: boolean;
  generalStoneTypeId?: string;
  calculationInputs: any;
  allEntries: ProductEntry[];
  totalAnnualProduction: number;
  totalRevenue: number;
  formData?: any; // Access to full form data to get material entries
}

export default function ExtractionProductForm({
  formEntry: formEntryProp,
  onSave,
  onClose,
  editing,
  generalStoneTypeId,
  calculationInputs,
  allEntries,
  totalAnnualProduction,
  totalRevenue,
  formData,
}: ExtractionProductFormProps) {
  const { t } = useLanguage();

  const [formEntry, setFormEntry] = useState(formEntryProp);

  useEffect(() => {
    setFormEntry(formEntryProp);
  }, [formEntryProp]);

  const { touched, errors, validateForm, handleFieldBlur, isFormValid, markAllFieldsTouched } =
    useProductFormValidation("extraction");

  // Fetch stone types
  const { data: stoneTypes } = useData<StoneType>({
    collectionName: "setup_stoneTypes",
  });

  // Fetch specific stone type if we have a generalStoneTypeId
  const { document: generalStoneType } = useData<StoneType>({
    collectionName: "setup_stoneTypes",
    documentId: generalStoneTypeId || "",
  });

  const activeStoneTypes = stoneTypes || [];

  // Get stone type data
  const selectedStoneType: StoneType | null =
    activeStoneTypes.find((st) => st.id === formEntry.stoneType) ||
    (generalStoneTypeId && generalStoneType ? generalStoneType : null);

  // Get stone density
  const stoneDensity = selectedStoneType?.dens_pedra?.value || 0;

  // Update density when stone type changes
  useEffect(() => {
    if (formEntry.density !== stoneDensity) {
      setFormEntry((prev) => ({
        ...prev,
        density: stoneDensity,
      }));
    }
  }, [stoneDensity, formEntry.density]);

  // Default to extraction stone type if available
  useEffect(() => {
    if (generalStoneTypeId && !formEntry.stoneType && activeStoneTypes.length > 0) {
      const selectedStone = activeStoneTypes.find((st) => st.id === generalStoneTypeId);

      setFormEntry((prev) => ({
        ...prev,
        stoneType: generalStoneTypeId,
        stoneTypeName: selectedStone?.name || "",
      }));
    }
  }, [generalStoneTypeId, activeStoneTypes.length, formEntry.stoneType]);

  // Ensure stone type name is populated for existing entries
  useEffect(() => {
    if (formEntry.stoneType && !formEntry.stoneTypeName && activeStoneTypes.length > 0) {
      const selectedStone = activeStoneTypes.find((st) => st.id === formEntry.stoneType);
      if (selectedStone && selectedStone.name !== formEntry.stoneTypeName) {
        setFormEntry((prev) => ({
          ...prev,
          stoneTypeName: selectedStone.name,
        }));
      }
    }
  }, [activeStoneTypes.length, formEntry.stoneType, formEntry.stoneTypeName]);

  // Get selected stone type name for display
  const selectedStoneTypeName = selectedStoneType?.name || "";

  const { data: materialDocs } = useData<MaterialDocWithId>({
    collectionName: "setup_materials",
  });
  const { data: stoneDocs } = useData<StoneType>({
    collectionName: "setup_stoneTypes",
  });

  // Decide allocation type for extraction based on decision ratio (rd)
  const getAllocationType = (gd: any): "physical" | "economic" => {
    const pvpCaro = gd?.pvp_caro_ext || 0;
    const pvpBarato = gd?.pvp_barato_ext || 0;
    if (pvpBarato <= 0) return "economic";
    const rd = pvpCaro / pvpBarato;
    return rd <= 5 ? "physical" : "economic";
  };

  const handleSave = () => {
    markAllFieldsTouched();
    if (validateForm(formEntry) && materialDocs && stoneDocs) {
      // Calculate ratios
      // Convert current product's production to tonnes for accurate ratio calculation
      const productionInTonnes = convertToTonnes(formEntry).value;

      const calculatedProductRevenue = calculateProductRevenue(
        formEntry.annualProduction,
        totalRevenue,
        totalAnnualProduction
      );
      const ratioByQuantity = calculateRatioByQuantity(productionInTonnes, totalAnnualProduction);
      const ratioByRevenue = calculateRatioByRevenue(formEntry.productRevenue, totalRevenue);

      // Allocate material quantities proportionally based on the selected allocation ratio
      const allocationType = getAllocationType(calculationInputs?.generalData || {});

      // Helper to find annual material entry and products using a material
      const getAnnualMaterialData = (material: any) => {
        const activityType = calculationInputs?.generalData?.activityType;
        let materialEntries: any[] = [];

        if (activityType === "extraction_and_transformation") {
          materialEntries = formData?.annualConsumptions?.extraction?.materials?.entries || [];
        } else {
          materialEntries =
            formData?.materials?.entries || formData?.annualConsumptions?.extraction?.materials?.entries || [];
        }

        const annualMaterialEntry = materialEntries.find((entry: any) => {
          const byId = entry?.setup_item_id === material.materialId;
          const bySetupName = (entry?.setup_item_name || "").toLowerCase() === (material.materialName || "").toLowerCase();
          const byType = (entry?.materialType || "").toLowerCase() === (material.materialName || "").toLowerCase();
          const byName = (entry?.materialName || "").toLowerCase() === (material.materialName || "").toLowerCase();
          return byId || bySetupName || byType || byName;
        });

        const entriesForAllocation = editing ? allEntries : [...allEntries, formEntry];
        const productsUsingMaterial = entriesForAllocation.filter((entry) =>
          entry.productFlow === "extraction" &&
          entry.materialsUsed.some(
            (mat) => mat.materialId === material.materialId || mat.materialName === material.materialName
          )
        );

        return { annualMaterialEntry, productsUsingMaterial };
      };

      // Allocate materials for carbon (rd-based allocation)
      const materialsWithQuantities = formEntry.materialsUsed.map((material) => {
        if (editing && material.quantity > 0) {
          return material;
        }

        const { annualMaterialEntry, productsUsingMaterial } = getAnnualMaterialData(material);

        if (annualMaterialEntry && annualMaterialEntry.quantity !== undefined) {
          if (productsUsingMaterial.length === 0) {
            return { ...material, quantity: 0, unit: annualMaterialEntry.unit || material.unit };
          }

          let totalForMaterial = 0;
          let currentProductValue = 0;

          if (allocationType === "physical") {
            totalForMaterial = productsUsingMaterial.reduce((sum, entry) => sum + convertToTonnes(entry).value, 0);
            currentProductValue = productionInTonnes;
          } else {
            totalForMaterial = productsUsingMaterial.reduce((sum, entry) => sum + (Number(entry.productRevenue) || 0), 0);
            currentProductValue = Number(formEntry.productRevenue) || 0;
          }

          const materialSpecificRatio = totalForMaterial > 0 ? currentProductValue / totalForMaterial : 0;
          const allocatedQuantity = Number(annualMaterialEntry.quantity) * materialSpecificRatio;

          return { ...material, quantity: allocatedQuantity, unit: annualMaterialEntry.unit || material.unit };
        }
        return material;
      });

      // Allocate materials for hydric (ALWAYS economic allocation)
      const materialsUsedHydric = allocationType === "economic"
        ? materialsWithQuantities // Same as carbon when already economic
        : formEntry.materialsUsed.map((material) => {
            if (editing && material.quantity > 0) {
              // Re-allocate with economic ratio even for editing
            }

            const { annualMaterialEntry, productsUsingMaterial } = getAnnualMaterialData(material);

            if (annualMaterialEntry && annualMaterialEntry.quantity !== undefined) {
              if (productsUsingMaterial.length === 0) {
                return { ...material, quantity: 0, unit: annualMaterialEntry.unit || material.unit };
              }

              // Always use economic (revenue-based) allocation for hydric
              const totalForMaterial = productsUsingMaterial.reduce((sum, entry) => sum + (Number(entry.productRevenue) || 0), 0);
              const currentProductValue = Number(formEntry.productRevenue) || 0;
              const materialSpecificRatio = totalForMaterial > 0 ? currentProductValue / totalForMaterial : 0;
              const allocatedQuantity = Number(annualMaterialEntry.quantity) * materialSpecificRatio;

              return { ...material, quantity: allocatedQuantity, unit: annualMaterialEntry.unit || material.unit };
            }
            return material;
          });

      const entryWithRatios = {
        ...formEntry,
        materialsUsed: materialsWithQuantities,
        materialsUsedHydric,
        calculatedProductRevenue,
        ratioByQuantity,
        ratioByRevenue,
      };

      const footprints = calculateExtractionProductFootprints(
        entryWithRatios,
        allEntries,
        calculationInputs,
        materialDocs,
        stoneDocs
      );
      const mappedEntry = {
        ...entryWithRatios,
        ...footprints,
        carbonFootprintMaterials: footprints.pc_ext_materiais,
        carbonFootprintElectricity: footprints.pc_ext_ecr,
        carbonFootprintElectricityOnly: footprints.pc_ele || 0,
        carbonFootprintFuels: footprints.pc_comb || 0,
        carbonFootprintResidue: footprints.pc_res || 0,
        carbonFootprintTotal: footprints.pc_prod,
        hydricFootprintMaterials: footprints.ph_ext_materiais,
        hydricFootprintElectricity: footprints.ph_ext_ec,
        hydricFootprintWater: footprints.ph_ext_agua,
        hydricFootprintTotal: footprints.ph_prod,
      };
      onSave(mappedEntry);
    }
  };

  const onFieldBlur = (field: string) => {
    handleFieldBlur(field, formEntry);
  };

  // Extract available material IDs from annual consumption data
  const getAvailableMaterialIds = (): string[] => {
    if (!formData) return [];

    const activityType = calculationInputs?.generalData?.activityType;
    let materialEntries: any[] = [];

    if (activityType === "extraction_and_transformation") {
      // Mixed company - check extraction materials
      materialEntries = formData.annualConsumptions?.extraction?.materials?.entries || [];
    } else {
      // Single activity company - check both possible structures
      materialEntries =
        formData.materials?.entries || formData.annualConsumptions?.extraction?.materials?.entries || [];
    }

    // Extract material IDs from material entries (check multiple possible field names)
    const materialIds = materialEntries
      .filter((entry: any) => {
        // Only include entries that have a material type/name (actual materials, not empty entries)
        return entry.materialType && entry.materialType.trim() !== "";
      })
      .map((entry: any) => {
        // For materials, we need to find the setup_item_id from the material name
        // This will be resolved when we fetch the material document
        return entry.materialType;
      });

    return materialIds;
  };

  return (
    <div className="space-y-6 py-4">
      {/* Stone Type - Always disabled for extraction */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Label htmlFor="stoneType" className={`font-medium ${errors.stoneType ? "text-red-500" : "text-gray-700"}`}>
            {t("footprints.generalData.stoneType")} *
          </Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-gray-400 cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <p>{t("products.stoneTypeTooltip")}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <Input value={selectedStoneTypeName} disabled className="h-11 bg-gray-50 text-gray-600 cursor-not-allowed" />
        {touched.stoneType && errors.stoneType && (
          <div className="flex items-center gap-1 text-red-500 text-sm">
            <AlertCircle className="h-4 w-4" />
            <span>{errors.stoneType}</span>
          </div>
        )}
      </div>

      {/* Common Form Fields */}
      <CommonFormFields
        formEntry={formEntry}
        onFormEntryChange={setFormEntry}
        touched={touched}
        errors={errors}
        onFieldBlur={onFieldBlur}
        activityType="extraction"
        availableMaterialIds={getAvailableMaterialIds()}
      />

      {/* Dialog Footer */}
      <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
        <Button variant="outline" onClick={onClose}>
          {t("common.cancel")}
        </Button>
        <Button
          onClick={handleSave}
          className="bg-teal-600 hover:bg-teal-700 text-white"
          disabled={!isFormValid(formEntry)}
        >
          <Save className="w-4 h-4 mr-2" />
          {editing ? t("common.saveChanges") : t("common.add")}
        </Button>
      </div>
    </div>
  );
}
